import React, { cloneElement, isValidElement, useRef, useState } from 'react';

import { Popover } from '@/shared/components/ui/popover/components/popover/popover';
import { TPopoverControllerProps } from '@/shared/components/ui/popover/components/popover-controller/popover-controller.types';
import { useHasPermission } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';

export const PopoverController: React.FC<TPopoverControllerProps> = ({
  trigger,
  children,
  handleClose,
  handleTriggerClick,
  permission,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const open = Boolean(anchorEl);
  const hasPermission = useHasPermission(permission);

  const _handleClose = () => {
    setAnchorEl(null);
    handleClose?.();
  };

  const _handleClick = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    setAnchorEl(triggerRef.current);
    handleTriggerClick?.(event);
  };

  const renderTrigger = () => {
    const triggerElement =
      typeof trigger === 'function' ? trigger(open) : trigger;

    if (!isValidElement(triggerElement)) {
      console.error('Trigger must be a valid React element');
      return null;
    }

    return cloneElement(triggerElement, {
      ...triggerElement.props,
      ref: triggerRef,
      clicked: open,
      onClick: (event: React.BaseSyntheticEvent) => {
        event.preventDefault();
        _handleClick(event);
        triggerElement.props.onClick?.(event);
      },
    });
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <>
      {renderTrigger()}
      <Popover
        open={open}
        onClick={(event) => event.stopPropagation()}
        anchorEl={anchorEl}
        onClose={_handleClose}
        triggerRef={triggerRef}
        {...rest}
      >
        {children(_handleClose)}
      </Popover>
    </>
  );
};
